<template>
  <div class="container">
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading" class="columns column-reverse">
      <div class="column is-6">
        <div v-if="isInstantQuote" class="ls-form-header">
          <h1>
            Instant Quote for
            <h1 class="has-text-weight-bold">{{ selectedService.name }}</h1>
          </h1>
        </div>
        <div v-else class="ls-form-header">
          <h1 class="has-text-weight-bold">
            Add Service&nbsp;/
            <h1>{{ selectedService.name }}</h1>
          </h1>
          <LsTypicalPricing
            v-if="showPriceRange"
            test-id="typical_pricing"
            :property-id="property_id"
            :service-id="service_id"
            class="ls-typical-pricing"
          />
          <LsClickable
            track-id="order_services_form-how_quoting_works"
            class="ls-how-quoting-works"
            @click="openModal"
          >
            <div class="ls-question-circle">?</div>
            <span>See how quoting works</span>
          </LsClickable>
        </div>
        <InstantQuoteBushTrimmingForm v-if="isBushTrimming" test-id="instant_quote_bt_form" />
        <InstantQuoteFlowerBedWeedingForm
          v-else-if="isFlowerBedWeeding"
          test-id="instant_quote_fbw_form"
        />
        <MulchingForm v-else-if="isMulching" />
        <InstantQuoteLeafRemovalForm
          v-else-if="isLeafRemoval"
          test-id="instant_quote_leaf_removal_form"
        />
        <CleanupForm v-else-if="isCleanup" test-id="cleanup_form" />
        <GenericServiceForm v-else />
      </div>
      <div class="column ls-address-column">
        <div class="ls-address-card"><LsAddressCard :property="selectedProperty" /></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import _ from 'lodash';
import MulchingForm from './MulchingForm/MulchingForm.vue';
import CleanupForm from './CleanupForm/CleanupForm.vue';
import InstantQuoteBushTrimmingForm from './InstantQuoteBushTrimmingForm/InstantQuoteBushTrimmingForm.vue';
import InstantQuoteLeafRemovalForm from './InstantQuoteLeafRemovalForm/InstantQuoteLeafRemovalForm.vue';
import InstantQuoteFlowerBedWeedingForm from './InstantQuoteFlowerBedWeedingForm/InstantQuoteFlowerBedWeedingForm.vue';
import GenericServiceForm from './GenericServiceForm/GenericServiceForm.vue';
import { LsAddressCard, LsClickable } from '@/components/common';
import { LsTypicalPricing } from '@/components';
import {
  ORDER_SERVICES__SELECTED_SERVICE,
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  ORDER_SERVICES__GET_SERVICES,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
} from '@/store/types';
import { lsModalService, lsRouterHelpers } from 'ls-vue-common';
import { HowQuotingWorksModal } from '@/modals';

export default {
  name: 'OrderServicesFormView',
  components: {
    MulchingForm,
    CleanupForm,
    InstantQuoteBushTrimmingForm,
    InstantQuoteLeafRemovalForm,
    InstantQuoteFlowerBedWeedingForm,
    GenericServiceForm,
    LsAddressCard,
    LsClickable,
    LsTypicalPricing,
  },
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      service_id: lsRouterHelpers.getParam(this.$route, 'service_id'),
      instant_quote_suggestion_id: lsRouterHelpers.getParam(
        this.$route,
        'instant_quote_suggestion_id'
      ),
    };
  },
  computed: {
    ...mapState(['featureFlags', 'orderServices', 'properties']),
    ...mapGetters([ORDER_SERVICES__SELECTED_SERVICE, PROPERTIES__SELECTED_PROPERTY]),
    isLoading() {
      return this.properties.loadingStatus.isLoading || this.orderServices.loadingStatus.isLoading;
    },
    showPriceRange() {
      return _.get(this.featureFlags.flags, ['customer.manualquotes.show_pricerange']);
    },
    selectedService() {
      return this[ORDER_SERVICES__SELECTED_SERVICE](this.property_id, this.service_id) || {};
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    isInstantQuote() {
      return this.isBushTrimming || this.isFlowerBedWeeding || this.isLeafRemoval;
    },
    isBushTrimming() {
      return Boolean(_.get(this.selectedService, 'str_id') === 'bushtrimming');
    },
    isFlowerBedWeeding() {
      return Boolean(_.get(this.selectedService, 'str_id') === 'bed_weeding');
    },
    isMulching() {
      return (
        Boolean(_.get(this.selectedService, 'str_id') === 'mulching') &&
        _.get(this.featureFlags.flags, ['customer.manualquotes.questions.mulching'])
      );
    },
    isLeafRemoval() {
      return Boolean(_.get(this.selectedService, 'str_id') === 'cleanup_leaves');
    },
    isCleanup() {
      return (
        Boolean(_.get(this.selectedService, 'str_id') === 'cleanup_spring') &&
        _.get(this.featureFlags.flags, ['customer.manualquotes.questions.cleanup'])
      );
    },
  },
  async created() {
    this[FEATURE_FLAGS__REGISTER_EXPOSURE]({ flag: 'customer.manualquotes.show_pricerange' });

    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();
    await this[ORDER_SERVICES__GET_SERVICES]({ property_id: this.property_id });

    if (_.isEmpty(this.selectedService)) {
      return this.$router.push({
        name: 'services',
      });
    }
  },
  methods: {
    ...mapActions([
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      ORDER_SERVICES__GET_SERVICES,
      FEATURE_FLAGS__REGISTER_EXPOSURE,
    ]),
    openModal() {
      lsModalService.open({
        parent: this,
        component: HowQuotingWorksModal,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  h1 {
    display: inline;
  }
}

.ls-form-header {
  margin-bottom: 30px;

  .ls-typical-pricing {
    margin: 5px 0;
  }

  .ls-how-quoting-works {
    padding-top: 5px;
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: $size-5;
    color: $blue;

    .ls-question-circle {
      margin-right: 8px;
      color: $white;
      height: 26px;
      width: 26px;
      background: $blue;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }

    &:hover {
      color: $gray-primary;
    }
  }
}

.column-reverse {
  @media (max-width: $mobile-max-width) {
    display: flex;
    flex-direction: column-reverse;
  }
}

.ls-address-card {
  @media (max-width: $mobile-max-width) {
    padding-bottom: 15px;
    border-bottom: 1px solid $primary-light;
  }
}

.ls-address-column {
  @media (min-width: $desktop) {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
  }
}

.themed-app {
  .ls-form-header {
    .ls-how-quoting-works {
      font-size: $theme-txt-body;
      color: $theme-color-blue;

      .ls-question-circle {
        color: $theme-color-white;
        background: $theme-color-blue;
      }

      &:hover {
        color: $theme-color-gray-darker;
      }
    }
  }

  .ls-address-card {
    @media (max-width: $mobile-max-width) {
      border-bottom-color: $theme-color-primary-light;
    }
  }
}
</style>
